/**
 * Pure row-validity rule for the AI order parser review table. Kept free of Angular
 * DI so it is unit-testable. Backend catalog facts (validationIssues) are merged with
 * the frontend-only quantity and customer-price gates into a single ordered,
 * de-duplicated fault list. A row is valid when the list is empty.
 */
export const AI_PARSER_ROW_FAULT = {
  MISSING_PRODUCT_ID: 'MISSING_PRODUCT_ID',
  PRODUCT_NOT_FOUND: 'PRODUCT_NOT_FOUND',
  AMBIGUOUS_CUSTOMER_PRODUCT_ID: 'AMBIGUOUS_CUSTOMER_PRODUCT_ID',
  PRODUCT_BLOCKED: 'PRODUCT_BLOCKED',
  PRODUCT_UNAVAILABLE: 'PRODUCT_UNAVAILABLE',
  INVALID_QUANTITY: 'INVALID_QUANTITY',
  INVALID_PRICE: 'INVALID_PRICE',
  NO_PRICE: 'NO_PRICE'
} as const;

/** Fault code → remote-i18n translation key. */
export const AI_PARSER_ROW_FAULT_KEYS: Record<string, string> = {
  [AI_PARSER_ROW_FAULT.MISSING_PRODUCT_ID]: 'AI_PARSER_ROW_MISSING_PRODUCT_ID',
  [AI_PARSER_ROW_FAULT.PRODUCT_NOT_FOUND]: 'AI_PARSER_ROW_PRODUCT_NOT_FOUND',
  [AI_PARSER_ROW_FAULT.AMBIGUOUS_CUSTOMER_PRODUCT_ID]: 'AI_PARSER_ROW_AMBIGUOUS_CUSTOMER_PRODUCT_ID',
  [AI_PARSER_ROW_FAULT.PRODUCT_BLOCKED]: 'AI_PARSER_ROW_PRODUCT_BLOCKED',
  [AI_PARSER_ROW_FAULT.PRODUCT_UNAVAILABLE]: 'AI_PARSER_ROW_UNAVAILABLE',
  [AI_PARSER_ROW_FAULT.INVALID_QUANTITY]: 'AI_PARSER_ROW_INVALID_QUANTITY',
  [AI_PARSER_ROW_FAULT.INVALID_PRICE]: 'AI_PARSER_ROW_INVALID_PRICE',
  [AI_PARSER_ROW_FAULT.NO_PRICE]: 'AI_PARSER_ROW_NO_PRICE'
};

/**
 * Faults the "submit despite invalid rows" override must never wave through: an order must not
 * carry a negative price or a quantity of zero, empty or negative, and a product the catalog
 * cannot match has nothing to post against.
 * Every other fault is a judgement call the reviewer is allowed to take responsibility for.
 */
export const AI_PARSER_UNOVERRIDABLE_FAULTS: string[] = [
  AI_PARSER_ROW_FAULT.MISSING_PRODUCT_ID,
  AI_PARSER_ROW_FAULT.PRODUCT_NOT_FOUND,
  AI_PARSER_ROW_FAULT.AMBIGUOUS_CUSTOMER_PRODUCT_ID,
  AI_PARSER_ROW_FAULT.INVALID_QUANTITY,
  AI_PARSER_ROW_FAULT.INVALID_PRICE
];

/**
 * The control a fault belongs to, so its message lands on the field that caused it rather than
 * in one pile under the product id. Price faults pick their column in the component: the
 * document price when that is the negative number, the customer price otherwise.
 */
export type AiParserFaultField = 'product' | 'quantity' | 'price';

export const AI_PARSER_ROW_FAULT_FIELDS: Record<string, AiParserFaultField> = {
  [AI_PARSER_ROW_FAULT.MISSING_PRODUCT_ID]: 'product',
  [AI_PARSER_ROW_FAULT.PRODUCT_NOT_FOUND]: 'product',
  [AI_PARSER_ROW_FAULT.AMBIGUOUS_CUSTOMER_PRODUCT_ID]: 'product',
  [AI_PARSER_ROW_FAULT.PRODUCT_BLOCKED]: 'product',
  [AI_PARSER_ROW_FAULT.PRODUCT_UNAVAILABLE]: 'product',
  [AI_PARSER_ROW_FAULT.INVALID_QUANTITY]: 'quantity',
  [AI_PARSER_ROW_FAULT.INVALID_PRICE]: 'price',
  [AI_PARSER_ROW_FAULT.NO_PRICE]: 'price'
};

export interface RowFaultInput {
  validationIssues?: string[];
  /** The id as it currently stands in the row's input. */
  productId?: string | null;
  /**
   * Whether that id matched a product in the synced collection — the same collection the row's
   * search picker reads, so an id it cannot find is one the reviewer cannot pick either.
   * undefined while no lookup has answered yet, so a row is never called not-found before
   * anything checked.
   */
  productFound?: boolean;
  quantity?: number | null;
  /** The document's own unit price. Negative is invalid; absent is normal (price list supplies it). */
  documentUnitPrice?: number | null;
  /**
   * Whether a delivery customer is selected at all. Only used to suppress NO_PRICE while a
   * lookup triggered by picking one is still in flight - a missing customer price on its own no
   * longer excuses the row once there's no document price either, since a manual entry can
   * supply the price regardless of whether a customer was ever picked.
   */
  customerSelected?: boolean;
  customerUnitPrice?: number | null;
  /**
   * The unit price this row would actually post with — the reviewer's document or manual
   * override, exactly as buildRowExtra writes it to extra.OrderRows.Price. Null when no override
   * applies and the ERP prices the row itself, in which case nothing the reviewer chose can be
   * negative. A negative document price the reviewer already repriced away is not a fault: it is
   * not what gets charged, and the table offers no way to edit it.
   */
  selectedUnitPrice?: number | null;
  pricesResolved: boolean;
  /**
   * Web checkout's stock verdict for the row (CheckoutQuantityValidationService, against the
   * availability the stock column shows) came back invalid. Undefined when stock isn't checked.
   */
  unavailable?: boolean;
}

export function computeRowFaults(input: RowFaultInput): string[] {
  // Id-level faults come first and alone: an empty or unknown id makes every downstream check
  // meaningless, and "not found" is a more useful thing to tell the reviewer than the stale
  // availability verdict the backend recorded for whatever id the row held before.
  if (input.productId != null && !input.productId.trim()) {
    return [AI_PARSER_ROW_FAULT.MISSING_PRODUCT_ID];
  }
  // The backend found the buyer's article number mapped to several of our articles and left the
  // printed id in place, so the local lookup cannot find it either: "pick one of these" is the
  // useful verdict, not "not found". It goes stale the moment the row's id resolves to a product.
  const ambiguous = (input.validationIssues || []).includes(AI_PARSER_ROW_FAULT.AMBIGUOUS_CUSTOMER_PRODUCT_ID);
  if (ambiguous && input.productFound !== true) {
    return [AI_PARSER_ROW_FAULT.AMBIGUOUS_CUSTOMER_PRODUCT_ID];
  }
  if (input.productFound === false) {
    return [AI_PARSER_ROW_FAULT.PRODUCT_NOT_FOUND];
  }

  // A backend catalog problem (missing id / not found / blocked) is the root cause: a row
  // whose product can't be resolved has no meaningful price, availability or quantity to
  // check against, so surface only those issues and don't pile on downstream faults.
  // PRODUCT_UNAVAILABLE is not a catalog problem — it comes from the frontend's own stock verdict
  // (input.unavailable), never the backend copy, and the reviewer can wave it through with the
  // invalid-row override.
  // INVALID_QUANTITY and INVALID_PRICE are excluded alongside PRODUCT_UNAVAILABLE because the
  // rules below own those codes: both are computed from values this table already holds, so a
  // backend copy goes stale the moment the reviewer edits the row, and inheriting it would keep
  // the row red until a server round-trip. If the value is still bad, the rule re-adds it.
  const backend = (input.validationIssues || []).filter((code, i, all) => all.indexOf(code) === i);
  const locallyOwned: string[] = [
    AI_PARSER_ROW_FAULT.PRODUCT_UNAVAILABLE,
    AI_PARSER_ROW_FAULT.INVALID_QUANTITY,
    AI_PARSER_ROW_FAULT.INVALID_PRICE
  ];
  const catalog = backend.filter(
    code => !locallyOwned.includes(code) && code !== AI_PARSER_ROW_FAULT.AMBIGUOUS_CUSTOMER_PRODUCT_ID
  );
  if (catalog.length > 0) {
    return catalog;
  }

  const faults: string[] = [];

  if (!input.quantity || input.quantity <= 0) {
    faults.push(AI_PARSER_ROW_FAULT.INVALID_QUANTITY);
  }

  // An order must never carry a negative price, so this is checked against the price that
  // actually posts rather than the document's number. A missing price is the ordinary case where
  // the PO carries no unit price and the customer's price list supplies it.
  if (input.selectedUnitPrice != null && input.selectedUnitPrice < 0) {
    faults.push(AI_PARSER_ROW_FAULT.INVALID_PRICE);
  }

  // A row with neither a document price nor a resolved/manual customer price has nothing
  // submittable, regardless of whether a delivery customer was ever picked - a manual entry can
  // supply the price on its own. Suppressed only while a lookup is genuinely still in flight
  // (customerSelected but not yet pricesResolved), so the fault doesn't flash on before the
  // lookup has had a chance to fill customerUnitPrice in.
  const hasCustomerPrice = input.customerUnitPrice != null && input.customerUnitPrice > 0;
  const hasDocumentPrice = input.documentUnitPrice != null && input.documentUnitPrice > 0;
  const lookupPending = !!input.customerSelected && !input.pricesResolved;
  if (!hasCustomerPrice && !hasDocumentPrice && !lookupPending) {
    faults.push(AI_PARSER_ROW_FAULT.NO_PRICE);
  }

  if (input.unavailable) {
    faults.push(AI_PARSER_ROW_FAULT.PRODUCT_UNAVAILABLE);
  }

  return faults;
}

/** Indicator state for the availability figure shown under the quantity input. */
export type AiParserAvailabilityState = 'ok' | 'incoming' | 'short';

/**
 * Pure indicator rule: green when current stock covers the requested quantity, orange
 * when short but more stock is incoming (maxFutureStock > currentAvailability), red when
 * short with nothing incoming. Null (no indicator) without availability data. A missing
 * or non-positive quantity compares as zero — the INVALID_QUANTITY fault owns that case.
 */
export function computeAvailabilityState(
  quantity: number | null | undefined,
  currentAvailability: number | null | undefined,
  maxFutureStock: number | null | undefined
): AiParserAvailabilityState | null {
  if (currentAvailability == null) {
    return null;
  }
  const qty = quantity || 0;
  if (currentAvailability >= qty) {
    return 'ok';
  }
  return (maxFutureStock ?? 0) > currentAvailability ? 'incoming' : 'short';
}
